import { Type } from 'typebox';
import { getLocalTimeZone } from '@tadpoles/common';
export const AuthLoginPayload = Type.Null();
export const AuthLoginRequest = Type.Object({
    email: Type.String({ minLength: 1 }),
    password: Type.String({ minLength: 1 }),
    service: Type.Optional(Type.String({ minLength: 1 })),
    sessionLength: Type.Optional(Type.Integer()),
    tz: Type.Optional(Type.String({ minLength: 1 }))
});
export const AuthGoogleTokenPayload = Type.Object({
    email: Type.String({ minLength: 1 }),
    status: Type.Literal('ok')
});
export const AuthGoogleTokenRequest = Type.Union([
    Type.Object({
        accessToken: Type.String({ minLength: 1 }),
        idToken: Type.Optional(Type.String({ minLength: 1 })),
        sessionLength: Type.Optional(Type.Integer())
    }),
    Type.Object({
        accessToken: Type.Optional(Type.String({ minLength: 1 })),
        idToken: Type.String({ minLength: 1 }),
        sessionLength: Type.Optional(Type.Integer())
    })
]);
export const AuthAppleTokenPayload = Type.Object({
    email: Type.String({ minLength: 1 }),
    status: Type.Literal('ok')
});
export const AuthAppleTokenRequest = Type.Object({
    appId: Type.String({ minLength: 1 }),
    authorizationCode: Type.String({ minLength: 1 }),
    redirectUri: Type.String(),
    sessionLength: Type.Optional(Type.Integer())
});
export const AuthAdmitRequest = Type.Object({
    tz: Type.String({ minLength: 1 })
});
export const AuthAdmitPayload = Type.Object({
    namespace: Type.String({ minLength: 1 })
});
export const AuthLogoutPayload = Type.Void();
export const AuthPasswordResetEmailPayload = Type.Null();
export const AuthPasswordResetEmailRequest = Type.Object({
    email: Type.String({ minLength: 1 })
});
export const AuthPasswordResetTokenValidationPayload = Type.Object({
    email: Type.String({ minLength: 1 })
});
export const AuthPasswordResetToken = Type.String({ minLength: 1 });
const AuthPasswordResetTokenValidationBody = Type.Object({
    token: AuthPasswordResetToken
});
export const AuthPasswordChangePayload = Type.Void();
export const AuthPasswordChangeRequest = Type.Object({
    currentPassword: Type.String({ minLength: 1 }),
    newPassword: Type.String({ minLength: 1 })
});
export const AuthPasswordResetWithTokenRequest = Type.Object({
    newPassword: Type.String({ minLength: 1 }),
    token: Type.String({ minLength: 1 })
});
export class HttpAuthResource {
    #httpClient;
    constructor(httpClient) {
        this.#httpClient = httpClient;
    }
    async login(request, options) {
        const service = request.service ?? 'tadpoles';
        const tz = request.tz ?? getLocalTimeZone();
        return await this.#httpClient.postForm({
            body: {
                ...request,
                service,
                tz
            },
            bodySchema: AuthLoginRequest,
            path: '/auth/login',
            payloadSchema: AuthLoginPayload,
            signal: options?.signal
        });
    }
    async loginWithGoogleToken(request, options) {
        return await this.#httpClient.post({
            body: request,
            bodySchema: AuthGoogleTokenRequest,
            path: '/auth/google/token',
            payloadSchema: AuthGoogleTokenPayload,
            signal: options?.signal
        });
    }
    async loginWithAppleToken(request, options) {
        return await this.#httpClient.post({
            body: request,
            bodySchema: AuthAppleTokenRequest,
            path: '/auth/apple/token',
            payloadSchema: AuthAppleTokenPayload,
            signal: options?.signal
        });
    }
    async admit(tz = getLocalTimeZone(), options) {
        const body = {
            tz
        };
        return await this.#httpClient.post({
            body,
            bodySchema: AuthAdmitRequest,
            path: '/auth/family/admit',
            payloadSchema: AuthAdmitPayload,
            signal: options?.signal
        });
    }
    async requestPasswordReset(request, options) {
        return await this.#httpClient.post({
            body: request,
            bodySchema: AuthPasswordResetEmailRequest,
            path: '/auth/reset',
            payloadSchema: AuthPasswordResetEmailPayload,
            signal: options?.signal
        });
    }
    async validatePasswordResetToken(token, options) {
        const body = { token };
        return await this.#httpClient.post({
            body,
            bodySchema: AuthPasswordResetTokenValidationBody,
            path: '/auth/reset/token',
            payloadSchema: AuthPasswordResetTokenValidationPayload,
            signal: options?.signal
        });
    }
    async changePassword(request, options) {
        return await this.#httpClient.post({
            body: request,
            bodySchema: AuthPasswordChangeRequest,
            path: '/auth/password',
            payloadSchema: AuthPasswordChangePayload,
            signal: options?.signal
        });
    }
    async resetPassword(request, options) {
        return await this.#httpClient.post({
            body: request,
            bodySchema: AuthPasswordResetWithTokenRequest,
            path: '/auth/password',
            payloadSchema: AuthPasswordChangePayload,
            signal: options?.signal
        });
    }
    async logout(options) {
        return await this.#httpClient.get({
            path: '/auth/logout',
            payloadSchema: AuthLogoutPayload,
            signal: options?.signal
        });
    }
}
